import React, { useState } from 'react';
import { Calendar, ShoppingBag, Users, Award, X, MapPin, Clock, Phone, Mail, ChevronRight, AlertCircle, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type Tournament = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  category: string;
  maxParticipants: number;
  currentParticipants: number;
  registrationDeadline: string;
  price: number;
  description: string;
};

type Coach = {
  id: string;
  name: string;
  title: string;
  specialties: string[];
  experience: string;
  rating: number;
  image: string;
  availability: {
    day: string;
    slots: string[];
  }[];
};

function Home() {
  const navigate = useNavigate();
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showTournamentsModal, setShowTournamentsModal] = useState(false);
  const [showCoachingModal, setShowCoachingModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    name: '',
    email: '',
    phone: '',
    partner: '',
    level: '',
    category: ''
  });

  const coaches: Coach[] = [
    {
      id: '1',
      name: 'Thomas Martin',
      title: 'Coach Principal',
      specialties: ['Technique avancée', 'Préparation physique', 'Stratégie de match'],
      experience: '15 ans d\'expérience en padel',
      rating: 4.9,
      image: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=format&fit=crop&q=80&w=400',
      availability: [
        {
          day: 'Lundi',
          slots: ['09:00', '10:30', '14:00', '15:30', '17:00']
        },
        {
          day: 'Mercredi',
          slots: ['09:00', '10:30', '14:00', '15:30', '17:00']
        },
        {
          day: 'Vendredi',
          slots: ['09:00', '10:30', '14:00', '15:30', '17:00']
        }
      ]
    },
    {
      id: '2',
      name: 'Sophie Dubois',
      title: 'Coach Elite',
      specialties: ['Formation junior', 'Technique de base', 'Tactique de jeu'],
      experience: '10 ans d\'expérience en padel',
      rating: 4.8,
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=400',
      availability: [
        {
          day: 'Mardi',
          slots: ['09:00', '10:30', '14:00', '15:30', '17:00']
        },
        {
          day: 'Jeudi',
          slots: ['09:00', '10:30', '14:00', '15:30', '17:00']
        },
        {
          day: 'Samedi',
          slots: ['10:00', '11:30', '14:00', '15:30']
        }
      ]
    },
    {
      id: '3',
      name: 'Marc Leroy',
      title: 'Coach Performance',
      specialties: ['Compétition', 'Mental training', 'Analyse vidéo'],
      experience: '12 ans d\'expérience en padel',
      rating: 4.7,
      image: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?auto=format&fit=crop&q=80&w=400',
      availability: [
        {
          day: 'Lundi',
          slots: ['14:00', '15:30', '17:00', '18:30']
        },
        {
          day: 'Jeudi',
          slots: ['14:00', '15:30', '17:00', '18:30']
        },
        {
          day: 'Samedi',
          slots: ['09:00', '10:30', '14:00', '15:30']
        }
      ]
    }
  ];

  const tournaments: Tournament[] = [
    {
      id: '1',
      name: 'Championnat Soavina Open',
      startDate: '2024-04-15',
      endDate: '2024-04-17',
      category: 'Mixte',
      maxParticipants: 32,
      currentParticipants: 24,
      registrationDeadline: '2024-04-10',
      price: 30,
      description: 'Le plus grand tournoi de padel de la saison. Ouvert à tous les niveaux.'
    },
    {
      id: '2',
      name: 'Tournoi des Débutants',
      startDate: '2024-05-01',
      endDate: '2024-05-02',
      category: 'Débutant',
      maxParticipants: 16,
      currentParticipants: 8,
      registrationDeadline: '2024-04-25',
      price: 20,
      description: 'Tournoi idéal pour les joueurs débutants souhaitant découvrir la compétition.'
    },
    {
      id: '3',
      name: 'Masters Soavina',
      startDate: '2024-06-10',
      endDate: '2024-06-12',
      category: 'Pro',
      maxParticipants: 24,
      currentParticipants: 16,
      registrationDeadline: '2024-06-05',
      price: 50,
      description: 'Tournoi élite réservé aux joueurs de niveau avancé.'
    }
  ];

  const handleRegistration = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate registration success
    alert('Inscription enregistrée avec succès !');
    setShowRegistrationForm(false);
    setSelectedTournament(null);
    setRegistrationData({
      name: '',
      email: '',
      phone: '',
      partner: '',
      level: '',
      category: ''
    });
  };

  const CoachingModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Cours & Coaching</h2>
          <button
            onClick={() => setShowCoachingModal(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Introduction */}
          <div>
            <h3 className="text-xl font-semibold mb-4">Nos formules de cours</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border rounded-lg p-6">
                <h4 className="text-lg font-semibold mb-2">Cours particulier</h4>
                <p className="text-gray-600 mb-4">Coaching personnalisé adapté à vos besoins</p>
                <ul className="space-y-2 text-gray-600 mb-4">
                  <li>• 1 personne : 45€/heure</li>
                  <li>• 2 personnes : 30€/heure/pers</li>
                </ul>
                <p className="text-sm text-gray-500">Inclus : Analyse vidéo et suivi personnalisé</p>
              </div>

              <div className="border rounded-lg p-6">
                <h4 className="text-lg font-semibold mb-2">Cours collectif</h4>
                <p className="text-gray-600 mb-4">Apprentissage en groupe, tous niveaux</p>
                <ul className="space-y-2 text-gray-600 mb-4">
                  <li>• 3-4 personnes : 20€/heure/pers</li>
                  <li>• Forfait 10 séances : 180€/pers</li>
                </ul>
                <p className="text-sm text-gray-500">Inclus : Matériel et analyse technique</p>
              </div>

              <div className="border rounded-lg p-6">
                <h4 className="text-lg font-semibold mb-2">Stage intensif</h4>
                <p className="text-gray-600 mb-4">Progression rapide sur 2-3 jours</p>
                <ul className="space-y-2 text-gray-600 mb-4">
                  <li>• 2 jours : 150€/pers</li>
                  <li>• 3 jours : 200€/pers</li>
                </ul>
                <p className="text-sm text-gray-500">Inclus : Repas et équipement</p>
              </div>
            </div>
          </div>

          {/* Coaches */}
          <div>
            <h3 className="text-xl font-semibold mb-6">Nos coachs</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {coaches.map((coach) => (
                <div key={coach.id} className="border rounded-lg overflow-hidden">
                  <img
                    src={coach.image}
                    alt={coach.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold">{coach.name}</h4>
                        <p className="text-gray-600 text-sm">{coach.title}</p>
                      </div>
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="ml-1 text-sm">{coach.rating}</span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-3">{coach.experience}</p>
                    
                    <div className="mb-4">
                      <h5 className="text-sm font-medium mb-2">Spécialités :</h5>
                      <div className="flex flex-wrap gap-2">
                        {coach.specialties.map((specialty, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="text-sm font-medium mb-2">Disponibilités :</h5>
                      <div className="space-y-2">
                        {coach.availability.map((schedule, index) => (
                          <div key={index} className="text-sm">
                            <span className="font-medium">{schedule.day}</span>
                            <span className="text-gray-600">
                              {' : '}
                              {schedule.slots.join(', ')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Booking CTA */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Prêt à commencer ?</h3>
              <p className="text-gray-600 mb-4">
                Réservez votre cours dès maintenant et commencez votre progression
              </p>
              <button
                onClick={() => {
                  setShowCoachingModal(false);
                  navigate('/booking');
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
              >
                Réserver un cours
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const TournamentsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Tournois à venir</h2>
          <button
            onClick={() => {
              setShowTournamentsModal(false);
              setSelectedTournament(null);
              setShowRegistrationForm(false);
            }}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {!showRegistrationForm ? (
            <div className="space-y-6">
              {tournaments.map((tournament) => (
                <div key={tournament.id} className="border rounded-lg p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-semibold">{tournament.name}</h3>
                      <p className="text-gray-600">{tournament.description}</p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {tournament.category}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="font-medium">
                        {new Date(tournament.startDate).toLocaleDateString()} - {new Date(tournament.endDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Inscription jusqu'au</p>
                      <p className="font-medium">{new Date(tournament.registrationDeadline).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Places</p>
                      <p className="font-medium">{tournament.currentParticipants}/{tournament.maxParticipants}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Prix</p>
                      <p className="font-medium">{tournament.price} €</p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setSelectedTournament(tournament);
                        setShowRegistrationForm(true);
                      }}
                      disabled={tournament.currentParticipants >= tournament.maxParticipants}
                      className={`px-6 py-2 rounded-md font-semibold ${
                        tournament.currentParticipants >= tournament.maxParticipants
                          ? 'bg-gray-300 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {tournament.currentParticipants >= tournament.maxParticipants
                        ? 'Complet'
                        : "S'inscrire"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <button
                onClick={() => setShowRegistrationForm(false)}
                className="mb-4 text-blue-600 hover:text-blue-800 flex items-center space-x-2"
              >
                <ChevronRight className="h-5 w-5 rotate-180" />
                <span>Retour aux tournois</span>
              </button>

              <h3 className="text-xl font-semibold mb-6">
                Inscription au {selectedTournament?.name}
              </h3>

              <form onSubmit={handleRegistration} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom complet
                    </label>
                    <input
                      type="text"
                      required
                      value={registrationData.name}
                      onChange={(e) => setRegistrationData({ ...registrationData, name: e.target.value })}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={registrationData.email}
                      onChange={(e) => setRegistrationData({ ...registrationData, email: e.target.value })}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      required
                      value={registrationData.phone}
                      onChange={(e) => setRegistrationData({ ...registrationData, phone: e.target.value })}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du partenaire
                    </label>
                    <input
                      type="text"
                      required
                      value={registrationData.partner}
                      onChange={(e) => setRegistrationData({ ...registrationData, partner: e.target.value })}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Niveau
                    </label>
                    <select
                      required
                      value={registrationData.level}
                      onChange={(e) => setRegistrationData({ ...registrationData, level: e.target.value })}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez votre niveau</option>
                      <option value="debutant">Débutant</option>
                      <option value="intermediaire">Intermédiaire</option>
                      <option value="avance">Avancé</option>
                      <option value="expert">Expert</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Catégorie
                    </label>
                    <select
                      required
                      value={registrationData.category}
                      onChange={(e) => setRegistrationData({ ...registrationData, category: e.target.value })}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez une catégorie</option>
                      <option value="mixte">Mixte</option>
                      <option value="homme">Homme</option>
                      <option value="femme">Femme</option>
                    </select>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-md flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Informations importantes</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Le paiement s'effectue sur place le jour du tournoi</li>
                      <li>Présentez-vous 30 minutes avant votre premier match</li>
                      <li>N'oubliez pas votre équipement</li>
                    </ul>
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowRegistrationForm(false)}
                    className="px-6 py-2 border rounded-md hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Confirmer l'inscription
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const AboutModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">À propos de Soavina Atmosphere Padel</h2>
          <button
            onClick={() => setShowAboutModal(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6 space-y-8">
          {/* Présentation */}
          <section>
            <h3 className="text-xl font-semibold mb-4">Notre Club</h3>
            <p className="text-gray-600 leading-relaxed">
              Soavina Atmosphere Padel est bien plus qu'un simple club de padel. C'est un lieu de rencontre 
              pour les passionnés de sport, offrant une expérience unique dans un cadre exceptionnel. 
              Nos installations modernes et notre équipe professionnelle vous garantissent des moments 
              inoubliables sur les terrains.
            </p>
          </section>

          {/* Installations */}
          <section>
            <h3 className="text-xl font-semibold mb-4">Nos Installations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h4 className="font-medium">Terrains</h4>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>2 terrains couverts</li>
                  <li>2 terrains extérieurs</li>
                  <li>Éclairage LED professionnel</li>
                  <li>Surface World Padel Tour homologuée</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">Commodités</h4>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Vestiaires avec douches</li>
                  <li>Casiers sécurisés</li>
                  <li>Espace détente climatisé</li>
                  <li>Parking gratuit</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Services */}
          <section>
            <h3 className="text-xl font-semibold mb-4">Nos Services</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Location de Matériel</h4>
                <p className="text-gray-600">Raquettes et accessoires disponibles à la location</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Cours & Coaching</h4>
                <p className="text-gray-600">Instructeurs certifiés pour tous les niveaux</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Événements</h4>
                <p className="text-gray-600">Tournois et animations réguliers</p>
              </div>
            </div>
          </section>

          {/* Tarifs */}
          <section>
            <h3 className="text-xl font-semibold mb-4">Nos Tarifs</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-2">Location de Terrain</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex justify-between">
                    <span>Heures creuses</span>
                    <span className="font-medium">20€/1h30</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Heures pleines</span>
                    <span className="font-medium">30€/1h30</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Week-end</span>
                    <span className="font-medium">35€/1h30</span>
                  </li>
                </ul>
              </div>
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-2">Abonnements</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex justify-between">
                    <span>Mensuel</span>
                    <span className="font-medium">80€/mois</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Trimestriel</span>
                    <span className="font-medium">220€/3 mois</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Annuel</span>
                    <span className="font-medium">800€/an</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Informations pratiques */}
          <section>
            <h3 className="text-xl font-semibold mb-4">Informations Pratiques</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-medium">Adresse</h4>
                    <p className="text-gray-600">123 Avenue du Padel<br />Antananarivo 101</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-medium">Horaires</h4>
                    <p className="text-gray-600">
                      Lundi - Vendredi : 6h - 22h<br />
                      Week-end : 8h - 20h
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-medium">Téléphone</h4>
                    <p className="text-gray-600">+261 34 12 345 67</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-medium">Email</h4>
                    <p className="text-gray-600">contact@soavinapadel.com</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => {
                setShowAboutModal(false);
                navigate('/booking');
              
              }}
              className="w-full sm:w-auto bg-blue-600 text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <span>Réserver maintenant</span>
              <ChevronRight className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                setShowAboutModal(false);
                navigate('/members');
              }}
              className="w-full sm:w-auto bg-white border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-md text-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Devenir membre
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="relative h-[600px]">
        <div className="absolute inset-0">
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&q=80&w=1920')`
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-5xl font-bold mb-4 leading-tight">
              Bienvenue à Soavina Atmosphere Padel
            </h1>
            <p className="text-xl mb-8 text-gray-200">
              Découvrez le padel dans un cadre exceptionnel. Une expérience unique pour tous les passionnés de sport.
            </p>
            <div className="space-x-4">
              <button 
                onClick={() => navigate('/booking')}
                className="bg-blue-600 text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Réserver un terrain
              </button>
              <button 
                onClick={() => setShowAboutModal(true)}
                className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
              >
                En savoir plus
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">Nos Services</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div 
            onClick={() => navigate('/booking')}
            className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-shadow cursor-pointer"
          >
            <Calendar className="h-12 w-12 mx-auto mb-4 text-blue-600" />
            <h3 className="text-xl font-semibold mb-2">Réservation en ligne</h3>
            <p className="text-gray-600">Réservez votre terrain en quelques clics</p>
          </div>
          
          <div 
            onClick={() => navigate('/shop')}
            className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-shadow cursor-pointer"
          >
            <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-blue-600" />
            <h3 className="text-xl font-semibold mb-2">Pro Shop</h3>
            <p className="text-gray-600">Équipement et accessoires de qualité</p>
          </div>
          
          <div 
            onClick={() => setShowCoachingModal(true)}
            className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-shadow cursor-pointer"
          >
            <Users className="h-12 w-12 mx-auto mb-4 text-blue-600" />
            <h3 className="text-xl font-semibold mb-2">Cours & Coaching</h3>
            <p className="text-gray-600">Apprenez avec nos experts qualifiés</p>
          </div>
          
          <div 
            onClick={() => setShowTournamentsModal(true)}
            className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-shadow cursor-pointer"
          >
            <Award className="h-12 w-12 mx-auto mb-4 text-blue-600" />
            <h3 className="text-xl font-semibold mb-2">Tournois</h3>
            <p className="text-gray-600">Participez à nos événements réguliers</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16">
        <div className="absolute inset-0">
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&q=80&w=1920')`
            }}
          ></div>
          <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">Prêt à commencer ?</h2>
          <p className="text-xl mb-8 text-gray-200">Rejoignez notre communauté de passionnés de padel</p>
          <div className="space-x-4">
            <button 
              onClick={() => navigate('/members')}
              className="bg-white text-blue-600 px-8 py-3 rounded-md text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Devenir membre
            </button>
            <button 
              onClick={() => navigate('/booking')}
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
            >
              Nos tarifs
            </button>
          </div>
        </div>
      </section>

      {/* Modals */}
      {showAboutModal && <AboutModal />}
      {showTournamentsModal && <TournamentsModal />}
      {showCoachingModal && <CoachingModal />}
    </div>
  );
}

export default Home;